import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError, formatMoney, formatDate, QUOTATION_STATUSES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import QuotationForm from "@/components/QuotationForm";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Quotations() {
  const { user } = useAuth();
  const isStaff = user.role !== "customer";
  const [quotations, setQuotations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await api.get("/quotations", { params });
      setQuotations(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isStaff) api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, [isStaff]);

  const create = async (payload) => {
    setSaving(true);
    try {
      await api.post("/quotations", payload);
      toast.success("Quotation created");
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="quotations-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Quotations</h1>
          <p className="mt-1 text-sm text-muted-foreground">RFQ responses with full revision history.</p>
        </div>
        {isStaff && (
          <Button onClick={() => setFormOpen(true)} data-testid="create-quotation-button">
            <Plus className="mr-2 h-4 w-4" /> New Quotation
          </Button>
        )}
      </div>

      <div className="w-48">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="quotation-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(QUOTATION_STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border" data-testid="quotations-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Quotation No.</TableHead>
              {isStaff && <TableHead>Customer</TableHead>}
              <TableHead>Version</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="hidden md:table-cell">Created</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></TableCell></TableRow>
            )}
            {!loading && quotations.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">No quotations found.</TableCell></TableRow>
            )}
            {quotations.map((q) => (
              <TableRow key={q.id} data-testid={`quotation-row-${q.quotation_no}`}>
                <TableCell className="font-mono text-xs font-medium text-primary">{q.quotation_no}</TableCell>
                {isStaff && <TableCell>{q.customer_name}</TableCell>}
                <TableCell><span className="rounded-sm border border-border px-1.5 py-0.5 font-mono text-[10px]" data-testid={`quotation-version-${q.quotation_no}`}>v{q.version}</span></TableCell>
                <TableCell><StatusBadge status={q.status} label={QUOTATION_STATUSES[q.status]} /></TableCell>
                <TableCell className="text-right font-mono text-sm">{formatMoney(q.total, q.currency)}</TableCell>
                <TableCell className="hidden text-sm text-muted-foreground md:table-cell">{formatDate(q.created_at)}</TableCell>
                <TableCell>
                  <Link to={`/quotations/${q.id}`}>
                    <Button variant="ghost" size="icon" data-testid={`view-quotation-${q.quotation_no}`}><Eye className="h-4 w-4" /></Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <QuotationForm
        open={formOpen}
        onOpenChange={setFormOpen}
        customers={customers}
        saving={saving}
        onSubmit={create}
        title="New Quotation"
      />
    </div>
  );
}
